import type { FaqItem } from '@/app/(pepero-label)/_components/common/Faq';
import { PEPERO_LABEL_PRINT_LIMIT } from '@/app/(pepero-label)/_constants/label';

// 1:1 문의유형 이름(부분 일치). 2026-10-22 기준 dev·운영 공통코드에 아직 없다 — BE 추가 요청 필요
export const PEPERO_LABEL_QNA_CATEGORY = '빼빼로';

export const PEPERO_LABEL_LINKS = {
  home: '/pepero-label',
  create: '/pepero-label/create',
  createMethod: '/pepero-label/create/method',
  createPhoto: '/pepero-label/create/photo',
  createText: '/pepero-label/create/text',
  myLabel: '/pepero-label/my',
  // 인쇄 장소 안내·FAQ 는 노션 페이지 (notion-app/_lib/notion-config.ts PEPERO_LABEL)
  place: '/notion-app/pepero-label/place',
  faq: '/notion-app/pepero-label/faq',
  // 1:1 문의는 목록으로 (결과서 48행). 문의유형 '빼빼로…'가 공통코드(QC)에 생기면 목록·작성이 그 유형으로 고정된다
  inquiry: `/support/qna/list?categoryName=${encodeURIComponent(PEPERO_LABEL_QNA_CATEGORY)}`,
} as const;

// Figma con_1 633:3534 — 라벨 만들기 4단계. imageLeft 는 카드 기준 일러스트 x 좌표(시안 값 그대로)
export const PEPERO_LABEL_MAKE_STEPS = [
  { title: '빼빼로 플레이버를 골라요', description: ['6종의 빼빼로 플레이버 중', '하나를 선택하세요.'], image: 'step-1.webp', imageLeft: 174 },
  { title: '커스텀 방식을 골라요', description: ['사진 또는 문구 커스텀 중', '원하는 방식을 선택하세요.'], image: 'step-2.webp', imageLeft: 174 },
  { title: '나만의 라벨을 꾸며요', description: ['사진이나 문구를 넣고', '스티커로 꾸며보세요.'], image: 'step-3.webp', imageLeft: 164 },
  { title: '완성한 라벨을 저장해요', description: ['라벨을 다운로드하거나', '키오스크 인쇄 코드를 받으세요.'], image: 'step-4.webp', imageLeft: 178 },
] as const;

// Figma 633:3616 — 키오스크 인쇄 3단계. description 은 카드 폭(78px)에서 자연 줄바꿈
// 일러스트는 시안 78×60 슬롯 그대로 잘라 구운 이미지(3x 234×180)다 — 단계별 크롭·위치를 CSS 로 맞추지 않는다
export const PEPERO_LABEL_PRINT_STEPS = [
  {
    title: ['인쇄 코드', '받기'],
    description: '생성한 라벨 인쇄 코드를 받아주세요.',
    image: 'print-step-1.webp',
  },
  {
    title: ['키오스크에서', '출력하기'],
    description: '인쇄 코드를 입력하고 출력하세요.',
    image: 'print-step-2.webp',
  },
  {
    title: ['빼빼로에', '라벨 붙이기'],
    description: '출력한 라벨을 패키지에 붙여보세요.',
    image: 'print-step-3.webp',
  },
] as const;

// Figma pepero label_Event Venue 1039:38456 (빼빼로_행사진행 1039:38387). 기간은 기획 확정 일정(한상우 2026-10-22) — 웰푸드 최종 확인 전이라 바뀔 수 있다
export const PEPERO_LABEL_VENUE = {
  name: '세븐일레븐 NewWave명동점',
  address: '서울특별시 중구 퇴계로 139 1층(명동역 8번 출구 인근)',
  period: '10.26 ~ 11.11',
  hours: '00:00 ~24:00',
} as const;

// Figma 유의사항 1129:38260 — 문단·줄바꿈도 시안 그대로 (웰푸드 요청)
export const PEPERO_LABEL_FAQ: readonly FaqItem[] = [
  {
    q: '라벨 만들기 및 이용 안내',
    a: [
      '\'나만의 빼빼로 라벨 스티커 만들기\'는 코튼시드 회원가입 후 이용하실 수 있습니다.',
      '라벨 만들기는 2026년 11월 30일까지 운영됩니다.',
      '운영 기간은 당사 사정에 따라 변경될 수 있습니다.',
    ].join('\n'),
  },
  {
    q: '현장 인쇄 안내',
    a: [
      `인쇄 코드는 코튼시드 계정 1개당 최대 ${PEPERO_LABEL_PRINT_LIMIT}회까지 생성할 수 있습니다.`,
      // 횟수는 키오스크에 코드를 입력할 때 차감된다(BE getCodeImage) — '한 번 사용' 이 생성인지 입력인지 헷갈린다는 웰푸드 피드백으로 명확히 함
      '인쇄 코드는 생성 후 3분간 유효하며 키오스크에 코드를 입력 시 횟수가 차감됩니다.',
      '인쇄 코드는 현장 인쇄 시에만 사용 가능합니다.',
    ].join('\n'),
  },
  {
    q: '라벨 이미지 보관 안내',
    a: [
      '저장한 라벨 이미지는 \'내 라벨\'에서 저장일로부터 3개월간 보관됩니다.',
      '보관 기간이 지난 이미지는 자동 삭제되며 복구할 수 없습니다.',
    ].join('\n'),
  },
  {
    q: '인쇄 운영 기간 및 장소',
    a: [
      '- 세븐일레븐 NewWave명동점 : 26년 10월 26일 ~ 11월 11일',
      '인쇄 운영 기간은 당사 사정에 따라 변경될 수 있습니다.',
    ].join('\n'),
  },
];
